import { calgaryDateString } from '../../../../utils/calgaryTime';
import React, { useState, useEffect, useRef, useCallback } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSearch, faCheck, faPlus } from "@fortawesome/free-solid-svg-icons";
import { useQuery } from "@tanstack/react-query";
import FinancialService from "../../../../AppSettings/services/FinancialService";
import MatterService from "../../../../AppSettings/services/MatterService";
import EmployeeService from "../../../../AppSettings/services/EmployeeService";
import useActiveEmployeeId from "../../../../AppSettings/hooks/useActiveEmployeeId";
import useCurrentEmployee from "../../../../AppSettings/hooks/useCurrentEmployee";
import toastService from "../../../../AppSettings/services/ToastService";
import FormEntryMultilineTextbox from "@frontend/Components/Forms/Entry/MultilineTextbox/FormEntryMultilineTextbox";
import FormModal from "@frontend/Components/Modals/FormModal";
import { isFeatureEnabled } from "@frontend/AppSettings/environmentConfig";
import { useDayLocks } from "@frontend/Financial/TimesheetDayLocks/useDayLocks";
import { WEEKDAY_NAMES, formatMonthDay, weekdayOfDateKey } from "@frontend/Financial/TimesheetDayLocks/dayLockHelpers";
import "@frontend/Financial/TimesheetDayLocks/TimesheetDayLocks.css";
import "./LogBillableEntryModal.css";

const LOCKED_STATUSES = ["complete", "on hold"];

function isMatterLocked(status: string | undefined): boolean {
  if (!status) return false;
  const cleaned = status.toLowerCase().trim().replace(/^status:\s*/i, "");
  return LOCKED_STATUSES.includes(cleaned);
}

type EntryType = "general" | "timeEntry" | "fixedFee";

interface LogBillableEntryModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const LogBillableEntryModal: React.FC<LogBillableEntryModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
}) => {
  const employeeId = useActiveEmployeeId();
  const currentEmployee = useCurrentEmployee();
  const isTimesheetOnlyUser = (() => {
    const p = currentEmployee?.permish_id;
    const n = typeof p === "string" ? parseInt(p, 10) : p;
    return n === 2 || n === 3;
  })();
  const [entryType, setEntryType] = useState<EntryType>("general");
  const [matters, setMatters] = useState<any[]>([]);
  const [employee, setEmployee] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Time Entry fields
  const [date, setDate] = useState(calgaryDateString());
  // Timesheet day locks (plans/todo/timesheet-day-locks.md, decision h): this form has no cost-code
  // field, so it cannot build a request. A permish 2/3 user picking a locked date for a time entry is
  // pointed at the timesheet cell instead, and submit is disabled (the server would refuse anyway).
  // Locks apply to everyone but permish 1 — including a loaded employee with NULL permish (decision b).
  const isDayLockRestricted = (() => {
    if (!currentEmployee) return false;
    const p = currentEmployee.permish_id;
    return (typeof p === "string" ? parseInt(p, 10) : p) !== 1;
  })();
  const dayLocks = useDayLocks(isFeatureEnabled("timesheetDayLocks") && isOpen && isDayLockRestricted);
  const lockedWeekday =
    entryType === "timeEntry" && dayLocks.isDayLocked(date) ? weekdayOfDateKey(date) : null;
  // A locked DATE names its lock (plans/todo/timesheet-date-locks.md, F2); a weekday keeps its sentence.
  const lockedDateLabel = lockedWeekday !== null ? dayLocks.lockLabelFor(date) : null;
  const [hours, setHours] = useState("");
  const [selectedMatter, setSelectedMatter] = useState<any>(null);
  const [matterSearch, setMatterSearch] = useState("");
  const [workDescription, setWorkDescription] = useState("");
  const [isBillable, setIsBillable] = useState(true);
  const [showMatterDropdown, setShowMatterDropdown] = useState(false);

  // Fixed Fee: search filter and create form
  const [feeSearch, setFeeSearch] = useState("");
  const [showCreateFee, setShowCreateFee] = useState(false);
  const [newFeeCode, setNewFeeCode] = useState("");
  const [newNarrative, setNewNarrative] = useState("");
  const [newFees, setNewFees] = useState("");

  // Expense fields
  const [expenseDescription, setExpenseDescription] = useState("");
  const [expenseAmount, setExpenseAmount] = useState("");
  const [taxPercent, setTaxPercent] = useState("0");

  const matterInputRef = useRef<HTMLInputElement>(null);
  const matterDropdownRef = useRef<HTMLDivElement>(null);

  const {
    data: feeTemplates,
    isLoading: feesLoading,
    error: feesError,
    refetch: refetchFees,
  } = useQuery({
    queryKey: ["fixedFees"],
    queryFn: FinancialService.getFixedFees,
    enabled: isOpen && entryType === "fixedFee",
  });

  const rawFees = feeTemplates?.data ?? feeTemplates;
  const feeList = Array.isArray(rawFees) ? rawFees : [];
  const filteredFees = feeList.filter((t: any) => {
    if (!feeSearch) return true;
    const q = feeSearch.toLowerCase();
    return (
      t.fee_code?.toLowerCase().includes(q) ||
      t.narrative?.toLowerCase().includes(q)
    );
  });

  // Reset form when modal closes so the next open shows a clean form
  const resetForm = useCallback(() => {
    setEntryType("general");
    setError("");
    setDate(calgaryDateString());
    setHours("");
    setSelectedMatter(null);
    setMatterSearch("");
    setWorkDescription("");
    setIsBillable(true);
    setShowMatterDropdown(false);
    setFeeSearch("");
    setShowCreateFee(false);
    setNewFeeCode("");
    setNewNarrative("");
    setNewFees("");
    setExpenseDescription("");
    setExpenseAmount("");
    setTaxPercent("0");
  }, []);

  useEffect(() => {
    if (!isOpen) {
      resetForm();
    }
  }, [isOpen, resetForm]);

  // Fetch matters and employee data
  useEffect(() => {
    const fetchData = async () => {
      try {
        const mattersData = await MatterService.getMatters();
        setMatters(mattersData || []);
      } catch (error) {
        console.error("Error fetching matters:", error);
      }

      if (employeeId) {
        try {
          const employeeData = await EmployeeService.getEmployeeById(employeeId);
          setEmployee(employeeData);
        } catch (error) {
          console.error("Error fetching employee:", error);
        }
      }
    };

    fetchData();
  }, [employeeId]);

  // Close matter dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        matterDropdownRef.current &&
        matterInputRef.current &&
        !matterDropdownRef.current.contains(event.target as Node) &&
        !matterInputRef.current.contains(event.target as Node)
      ) {
        setShowMatterDropdown(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  // Filter matters based on search
  const filteredMatters = matters.filter((matter) =>
    `${matter.matterid}: ${matter.mattername || ""}`
      .toLowerCase()
      .includes(matterSearch.toLowerCase())
  );

  const handleMatterSelect = (matter: any) => {
    setSelectedMatter(matter);
    setMatterSearch(`${matter.matterid}: ${matter.mattername || ""}`);
    setShowMatterDropdown(false);
    if (isTimesheetOnlyUser && isMatterLocked(matter.status)) {
      setError("This project is locked (complete or on hold). You cannot log time.");
    } else {
      setError("");
    }
  };

  const formatTimeRecorded = (time: string): string => {
    const totalMinutes = parseFloat(time) * 60;
    const hours = Math.floor(totalMinutes / 60);
    const minutes = Math.floor(totalMinutes % 60);
    return `${hours < 10 ? "0" + hours : hours}:${
      minutes < 10 ? "0" + minutes : minutes
    }:00`;
  };

  const handleCreateFixedFee = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await FinancialService.addFixedFee({
        fee_code: newFeeCode,
        narrative: newNarrative,
        fees: parseFloat(newFees),
      });
      setNewFeeCode("");
      setNewNarrative("");
      setNewFees("");
      setShowCreateFee(false);
      refetchFees();
      toastService.success("Fixed fee created successfully.");
    } catch (err: any) {
      console.error("Error creating fixed fee:", err);
      const msg = err.message || "Failed to create fixed fee.";
      setError(msg);
      toastService.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleAssignFixedFee = async (template: any) => {
    if (!selectedMatter) {
      setError("Please select a matter first");
      return;
    }
    if (isTimesheetOnlyUser && isMatterLocked(selectedMatter.status)) {
      setError("This project is locked (complete or on hold). You cannot assign fees.");
      return;
    }
    setLoading(true);
    setError("");
    try {
      await FinancialService.addAssignedFixedFees({
        fee_code: template.fee_code,
        narrative: template.narrative,
        fees: template.fees,
        matterid: selectedMatter.matterid,
      });
      toastService.success("Fixed fee assigned successfully.");
      onSuccess();
      onClose();
    } catch (err: any) {
      console.error("Error assigning fixed fee:", err);
      const msg = err.message || "Failed to assign fixed fee.";
      setError(msg);
      toastService.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (isTimesheetOnlyUser && selectedMatter && isMatterLocked(selectedMatter.status)) {
      setError("This project is locked (complete or on hold). You cannot log time.");
      return;
    }

    setLoading(true);

    try {
      if (!selectedMatter) {
        setError("Please select a matter");
        setLoading(false);
        return;
      }

      if (entryType === "timeEntry") {
        if (!employee || !employee.billedrate) {
          setError("Employee data or billed rate is missing");
          setLoading(false);
          return;
        }

        const formattedTime = formatTimeRecorded(hours);

        const formData = {
          time_recorded: formattedTime,
          date_recorded: date,
          notes: workDescription,
          employeeid: employee.employeeid,
          matterid: selectedMatter.matterid,
          isBillable, // A3: carry the "billable to client" flag (was cosmetic before)
        };

        await FinancialService.logTime(formData);
      } else if (entryType === "general") {
        if (!expenseDescription || !expenseAmount || !employeeId) {
          setError("Please fill in all required fields");
          setLoading(false);
          return;
        }

        const formData = {
          description: expenseDescription,
          amount: parseFloat(expenseAmount),
          date: date,
          employeeid: employeeId,
          matterid: selectedMatter.matterid,
          tax_percent: parseFloat(taxPercent) || 0,
        };

        await FinancialService.addOtherBilling(formData);
      }

      toastService.success("Billable entry added successfully.");
      onSuccess();
      onClose();
    } catch (error: any) {
      console.error("Error adding billable entry:", error);
      const errorMessage =
        error.message || "Failed to add billable entry. Please try again.";
      setError(errorMessage);
      toastService.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <FormModal
      isOpen={isOpen}
      onClose={onClose}
      title="Log Billable Entry"
      size="medium"
    >
      {/* Entry Type Selection */}
      <div className="log-billable-entry-type-tabs">
          <button
            className={`log-billable-entry-tab ${
              entryType === "general" ? "active" : ""
            }`}
            onClick={() => setEntryType("general")}
          >
            General
          </button>
          <button
            className={`log-billable-entry-tab ${
              entryType === "timeEntry" ? "active" : ""
            }`}
            onClick={() => setEntryType("timeEntry")}
          >
            Time Entry
          </button>
          <button
            className={`log-billable-entry-tab ${
              entryType === "fixedFee" ? "active" : ""
            }`}
            onClick={() => setEntryType("fixedFee")}
          >
            Fixed Fee
          </button>
        </div>

        {error && <div className="log-billable-entry-error">{error}</div>}

        {/* Fixed Fee tab: catalog-based UI */}
        {entryType === "fixedFee" && (
          <div className="log-billable-fixed-fee-section">
            <div className="log-billable-entry-field">
              <label>Matter</label>
              <div className="log-billable-entry-search-container">
                <input
                  ref={matterInputRef}
                  type="text"
                  placeholder="Search matters..."
                  value={matterSearch}
                  onChange={(e) => {
                    setMatterSearch(e.target.value);
                    setShowMatterDropdown(true);
                  }}
                  onFocus={() => setShowMatterDropdown(true)}
                />
                <FontAwesomeIcon icon={faSearch} className="search-icon" />
                {showMatterDropdown && filteredMatters.length > 0 && (
                  <div
                    ref={matterDropdownRef}
                    className="log-billable-entry-dropdown"
                  >
                    {filteredMatters.slice(0, 10).map((matter) => (
                      <div
                        key={matter.matterid}
                        className="log-billable-entry-dropdown-item"
                        onClick={() => handleMatterSelect(matter)}
                      >
                        {matter.matterid}: {matter.mattername || ""}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="log-billable-entry-field">
              <label>Search Fees</label>
              <input
                type="text"
                value={feeSearch}
                onChange={(e) => setFeeSearch(e.target.value)}
                placeholder="Filter by code or narrative..."
              />
            </div>

            <div className="log-billable-fee-create-toggle">
              <button
                type="button"
                className="log-billable-fee-create-btn"
                onClick={() => setShowCreateFee(!showCreateFee)}
              >
                {showCreateFee ? "Cancel" : "Create New Fixed Fee"}
              </button>
            </div>

            {showCreateFee && (
              <form
                className="log-billable-fee-create-form"
                onSubmit={handleCreateFixedFee}
              >
                <div className="log-billable-entry-field">
                  <label>Fee Code</label>
                  <input
                    type="text"
                    value={newFeeCode}
                    onChange={(e) => setNewFeeCode(e.target.value)}
                    placeholder="e.g. CONV-01"
                    required
                  />
                </div>
                <div className="log-billable-entry-field">
                  <label>Narrative</label>
                  <input
                    type="text"
                    value={newNarrative}
                    onChange={(e) => setNewNarrative(e.target.value)}
                    placeholder="e.g. Standard Conveyancing Fee"
                    required
                  />
                </div>
                <div className="log-billable-entry-field">
                  <label>Amount</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={newFees}
                    onChange={(e) => setNewFees(e.target.value)}
                    placeholder="0.00"
                    required
                  />
                </div>
                <button
                  type="submit"
                  className="log-billable-entry-save"
                  disabled={loading}
                >
                  <FontAwesomeIcon icon={faCheck} />
                  Save Fixed Fee
                </button>
              </form>
            )}

            {feesLoading && (
              <p className="log-billable-entry-hint">Loading fixed fees...</p>
            )}
            {feesError && (
              <div className="log-billable-entry-error">Error loading fixed fees.</div>
            )}

            <div className="log-billable-fee-list">
              {filteredFees.length === 0 && !feesLoading && (
                <p className="log-billable-fee-empty">No fixed fees found. Create one above to get started.</p>
              )}
              {filteredFees.map((template: any) => (
                <div
                  key={template.fee_code}
                  className="log-billable-fee-item"
                >
                  <div className="log-billable-fee-item-info">
                    <span className="log-billable-fee-code">{template.fee_code}</span>
                    <span className="log-billable-fee-narrative">{template.narrative}</span>
                  </div>
                  <div className="log-billable-fee-item-right">
                    <span className="log-billable-fee-amount">${template.fees}</span>
                    <button
                      type="button"
                      className="log-billable-fee-assign-btn"
                      disabled={loading}
                      onClick={() => handleAssignFixedFee(template)}
                      title="Assign to this matter"
                    >
                      <FontAwesomeIcon icon={faPlus} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Form for General and Time Entry tabs */}
        {entryType !== "fixedFee" && (
          <form onSubmit={handleSubmit} className="log-billable-entry-form">
            {/* Common Fields */}
            <div className="log-billable-entry-field">
              <label>Date</label>
              <input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                required
              />
            </div>
              {lockedWeekday !== null && (
                <p className="tdl-modal-locked-note" role="status">
                  {lockedDateLabel !== null ? (
                    <>
                      {formatMonthDay(date)} is locked ({lockedDateLabel}). Click that cell on the timesheet to request time.
                    </>
                  ) : (
                    <>
                      {WEEKDAY_NAMES[lockedWeekday]}s are locked. Click the {WEEKDAY_NAMES[lockedWeekday]} cell on the timesheet
                      to request time.
                    </>
                  )}
                </p>
              )}

            <div className="log-billable-entry-field">
              <label>Matter</label>
              <div className="log-billable-entry-search-container">
                <input
                  ref={matterInputRef}
                  type="text"
                  placeholder="Search matters..."
                  value={matterSearch}
                  onChange={(e) => {
                    setMatterSearch(e.target.value);
                    setShowMatterDropdown(true);
                  }}
                  onFocus={() => setShowMatterDropdown(true)}
                  required
                />
                <FontAwesomeIcon icon={faSearch} className="search-icon" />
                {showMatterDropdown && filteredMatters.length > 0 && (
                  <div
                    ref={matterDropdownRef}
                    className="log-billable-entry-dropdown"
                  >
                    {filteredMatters.slice(0, 10).map((matter) => (
                      <div
                        key={matter.matterid}
                        className="log-billable-entry-dropdown-item"
                        onClick={() => handleMatterSelect(matter)}
                      >
                        {matter.matterid}: {matter.mattername || ""}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Time Entry Fields */}
            {entryType === "timeEntry" && (
              <>
                <div className="log-billable-entry-field">
                  <label>Hours</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="0.00"
                    value={hours}
                    onChange={(e) => setHours(e.target.value)}
                    required
                  />
                </div>

                <div className="log-billable-entry-field">
                  <label>Hourly Rate</label>
                  <input
                    type="text"
                    value="Resolved from your profile rate"
                    disabled
                  />
                  <p className="log-billable-entry-hint">
                    Your rate is resolved automatically from company rates.
                  </p>
                </div>

                <div className="log-billable-entry-field">
                  <label>Work Description</label>
                  <FormEntryMultilineTextbox
                    name="workDescription"
                    value={workDescription}
                    onChange={(e) => setWorkDescription(e.target.value)}
                    placeholder="Describe the work performed..."
                    rows={4}
                  />
                </div>

                <div className="log-billable-entry-checkbox">
                  <input
                    type="checkbox"
                    id="billable"
                    checked={isBillable}
                    onChange={(e) => setIsBillable(e.target.checked)}
                  />
                  <label htmlFor="billable">
                    This time is billable to client
                  </label>
                </div>
              </>
            )}

            {/* General Fields */}
            {entryType === "general" && (
              <>
                <div className="log-billable-entry-field">
                  <label>Description</label>
                  <input
                    type="text"
                    value={expenseDescription}
                    onChange={(e) => setExpenseDescription(e.target.value)}
                    placeholder="Enter expense description"
                    required
                  />
                </div>

                <div className="log-billable-entry-field">
                  <label>Amount</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={expenseAmount}
                    onChange={(e) => setExpenseAmount(e.target.value)}
                    placeholder="0.00"
                    required
                  />
                </div>

                <div className="log-billable-entry-field">
                  <label>Tax Percent (Optional)</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    max="100"
                    value={taxPercent}
                    onChange={(e) => setTaxPercent(e.target.value)}
                    placeholder="0"
                  />
                </div>
              </>
            )}

            {/* Action Buttons */}
            <div className="log-billable-entry-actions">
              <button
                type="button"
                className="log-billable-entry-cancel"
                onClick={onClose}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="log-billable-entry-save"
                disabled={loading || lockedWeekday !== null}
              >
                <FontAwesomeIcon icon={faCheck} />
                Save to Queue
              </button>
            </div>
          </form>
        )}
    </FormModal>
  );
};

export default LogBillableEntryModal;
